import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { minorUnitsToMajorUnits } from '@/lib/money';
import { usePromotionCodeList } from '@/lib/api/promotionAnalytics';

import type { PromotionCodeListRow } from '../types';

interface Props {
  storeId: string;
  currency: string;
  startDate: Date;
  endDate: Date;
}

/** Quote a CSV field when it contains a comma, quote or newline; double internal quotes. */
function escapeCsv(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Build the affiliate-payout CSV: every code of the store's (non-credit)
 * promotions, ranked by net revenue, including zero-redemption codes. Amounts
 * are emitted in major (decimal) units — not cents — so they open correctly in
 * Excel, using the same minor-unit conversion as the on-screen figures. A
 * currency column makes each row self-describing.
 */
function buildCsv(rows: PromotionCodeListRow[], currency: string): string {
  const header = ['Code', 'Promotion', 'Redemptions', 'Gross sales', 'Net sales', 'Currency'];
  const toAmount = (cents: number) =>
    currency ? String(minorUnitsToMajorUnits(cents, currency)) : String(cents);

  const lines = rows.map((r) =>
    [
      r.code,
      r.promotionName,
      String(r.redemptions),
      toAmount(r.discountedSales),
      toAmount(r.netSales),
      currency,
    ]
      .map(escapeCsv)
      .join(','),
  );

  return [header.join(','), ...lines].join('\r\n');
}

/**
 * Toolbar button that exports the per-code report (all promotions, broken down
 * by code) to CSV. The table view itself stays per-promotion; this only adds an
 * export. Data respects the active date range and currency.
 */
export function PromotionCodeExportButton({ storeId, currency, startDate, endDate }: Props) {
  const { t } = useTranslation('analytics');
  const { data, isFetching } = usePromotionCodeList(
    { storeId, startDate, endDate, currency: currency || undefined },
    { enabled: !!storeId },
  );

  const codes = data?.codes ?? [];
  const resolvedCurrency = data?.currency ?? currency ?? '';

  const handleExport = () => {
    // Prefix a BOM so Excel detects UTF-8 (preserves accented promotion names).
    const BOM = String.fromCharCode(0xfeff);
    const csv = BOM + buildCsv(codes, resolvedCurrency);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `promotion-codes-${startDate.toISOString().split('T')[0]}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-9"
      onClick={handleExport}
      disabled={isFetching || codes.length === 0}
    >
      <Download className="size-4" />
      {t('promotionAnalytics.exportCsv')}
    </Button>
  );
}
